import { CheckRunRecord, FailureCheckConclusions, QueuedCheckStatuses } from '@mesa/core/checks';
import { Link } from '@tanstack/react-router';
import {
  CheckCheckIcon,
  CircleSlashIcon,
  ClockIcon,
  Loader2Icon,
  RefreshCwIcon,
  RefreshCwOffIcon,
  XCircleIcon,
} from 'lucide-react';
import { Suspense, useMemo } from 'react';

import { StatusIcon } from '@/components/status-icon';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useChecks, useChecksMutations } from '@/features/pulls/services/checks';
import { cn } from '@/lib/utils';

const TOOLTIPS = {
  running: 'Checks Running',
  queued: 'Checks Queued',
  failed: 'Failed Checks',
  passing: 'All Checks Passing',
  noChecks: 'No Checks',
};

/**
 * Validates that a SHA is properly formatted
 */
function isValidSha(sha: string): boolean {
  if (!sha || typeof sha !== 'string') return false;
  if (sha.length < 10) return false;
  if (sha.startsWith('/')) return false;
  if (sha.includes(' ')) return false;
  return /^[a-f0-9]+$/i.test(sha);
}

export const PullChecksPopover = ({ owner, repo, sha }: { owner: string; repo: string; sha: string }) => {
  // Validate SHA before using it
  if (!isValidSha(sha)) {
    console.error('🚨 PullChecksPopover: Invalid SHA detected:', { sha, owner, repo });
    return <Skeleton className="w-28 rounded-md h-6" />;
  }

  return (
    <Suspense fallback={<Skeleton className="w-28 rounded-md h-6" />}>
      <PullChecksPopoverInner owner={owner} repo={repo} sha={sha} />
    </Suspense>
  );
};

export const CIStatusIcon = ({ owner, repo, sha }: { owner: string; repo: string; sha: string }) => {
  // Validate SHA before using it
  if (!isValidSha(sha)) {
    console.error('🚨 CIStatusIcon: Invalid SHA detected:', { sha, owner, repo });
    return (
      <div className="flex items-center gap-1 p-2 rounded-md w-fit">
        <CircleSlashIcon size={14} className="text-gray-500 shrink-0" />
      </div>
    );
  }

  return (
    <Suspense fallback={<Skeleton />}>
      <CIStatusIconInner owner={owner} repo={repo} sha={sha} />
    </Suspense>
  );
};

const CIStatusIconInner = ({ owner, repo, sha }: { owner: string; repo: string; sha: string }) => {
  const checks = useChecks(owner, repo, sha);
  const checksState = useChecksState(checks);

  return <CheckStatus {...checksState} hideText={true} />;
};

const PullChecksPopoverInner = ({ owner, repo, sha }: { owner: string; repo: string; sha: string }) => {
  const checks = useChecks(owner, repo, sha);
  const checksState = useChecksState(checks);
  const { reRunChecks, reRunFailedChecks } = useChecksMutations(owner, repo, sha);

  return (
    <Popover>
      <PopoverTrigger asChild disabled={!checksState.hasChecks}>
        <div>
          <CheckStatus {...checksState} hideText={false} />
        </div>
      </PopoverTrigger>
      {checksState.hasChecks && (
        <PopoverContent side="bottom" className="w-96 max-h-[50vh] p-2 flex flex-col gap-2 overflow-y-auto">
          <div className="w-full flex items-center justify-between">
            <Button variant="ghost" title="Rerun all checks" className="size-8" onClick={() => reRunChecks()}>
              <RefreshCwIcon className="size-4 opacity-80" />
            </Button>
            <Button variant="ghost" title="Rerun failed checks" className="size-8" onClick={() => reRunFailedChecks()}>
              <RefreshCwOffIcon className="size-4 opacity-80" />
            </Button>
          </div>
          <div className="w-full flex flex-col gap-2">
            {checksState.runningChecks.length > 0 &&
              checksState.runningChecks.map((check) => <CheckItem key={check.id} check={check} />)}
            {checksState.queuedChecks.length > 0 &&
              checksState.queuedChecks.map((check) => <CheckItem key={check.id} check={check} />)}
            {checksState.completedChecks.length > 0 &&
              checksState.completedChecks.map((check) => <CheckItem key={check.id} check={check} />)}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

function useChecksState(checks: CheckRunRecord[]) {
  const runningChecks = useMemo(() => checks?.filter((check) => check.status === 'IN_PROGRESS') ?? [], [checks]);
  const queuedChecks = useMemo(
    () => checks?.filter((check) => QueuedCheckStatuses.some((status) => status === check.status)) ?? [],
    [checks]
  );
  const completedChecks = useMemo(() => checks?.filter((check) => check.status === 'COMPLETED') ?? [], [checks]);
  const hasChecks = useMemo(
    () => runningChecks.length + queuedChecks.length + completedChecks.length > 0,
    [runningChecks, queuedChecks, completedChecks]
  );

  return {
    runningChecks,
    queuedChecks,
    completedChecks,
    hasChecks,
  };
}

const CheckStatus = ({
  hideText,
  hasChecks,
  runningChecks,
  queuedChecks,
  completedChecks,
}: {
  hideText: boolean;
  hasChecks: boolean;
  runningChecks: CheckRunRecord[];
  queuedChecks: CheckRunRecord[];
  completedChecks: CheckRunRecord[];
}) => {
  const hasFailedChecks = completedChecks.some(
    (check) => check.conclusion && FailureCheckConclusions.some((conclusion) => conclusion === check.conclusion)
  );

  return (
    <div
      className={cn('flex items-center gap-1 p-2 rounded-md cursor-pointer overflow-hidden w-fit', {
        'hover:bg-stone-200': !hideText,
      })}
    >
      {hasChecks ? (
        runningChecks.length > 0 ? (
          <div className="flex items-center gap-1 overflow-hidden">
            <Loader2Icon size={14} className="text-gray-500 animate-spin shrink-0" />
            {!hideText && <span className="text-xs text-blue-500 truncate">{TOOLTIPS.running}</span>}
          </div>
        ) : queuedChecks.length > 0 ? (
          <div className="flex items-center gap-1 overflow-hidden">
            <ClockIcon size={14} className="text-gray-500 shrink-0" />
            {!hideText && <span className="text-xs text-muted-foreground truncate">{TOOLTIPS.queued}</span>}
          </div>
        ) : hasFailedChecks ? (
          <div className="flex items-center gap-1 overflow-hidden">
            <XCircleIcon size={14} className="text-red-500 shrink-0" />
            {!hideText && <span className="text-xs text-muted-foreground truncate">{TOOLTIPS.failed}</span>}
          </div>
        ) : (
          <div className="flex items-center gap-1 overflow-hidden">
            <CheckCheckIcon size={14} className="text-emerald-500 shrink-0" />
            {!hideText && <span className="text-xs text-muted-foreground truncate">{TOOLTIPS.passing}</span>}
          </div>
        )
      ) : (
        <div className="flex items-center gap-1 overflow-hidden">
          <CircleSlashIcon size={14} className="text-gray-500 shrink-0" />
          {!hideText && <span className="text-xs text-muted-foreground truncate">{TOOLTIPS.noChecks}</span>}
        </div>
      )}
    </div>
  );
};

const CheckItem = ({ check }: { check: NonNullable<ReturnType<typeof useChecks>>[number] }) => {
  return (
    <Link
      key={check.id}
      className="flex items-center justify-between p-2 hover:bg-muted rounded-md transition-colors hover:cursor-pointer"
      target="_blank"
      rel="noopener noreferrer"
      to={check.htmlUrl}
    >
      <div className="flex items-center space-x-2">
        <StatusIcon status={check.status} conclusion={check.conclusion} />
        <span className="text-sm font-medium text-gray-800">{check.name}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {check.status === 'COMPLETED' && check.completedAt
          ? new Date(check.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : check.status.replace(/_/g, ' ')}
      </div>
    </Link>
  );
};
